
import React from 'react';
import ScrollReveal from './ScrollReveal';
import { Button } from './button';
import { Link } from 'react-router-dom';

interface PageHeroProps {
  title: string;
  subtitle: string;
  description: string;
  badge?: string;
  backgroundImage: string;
  ctaText?: string;
  ctaLink?: string;
  secondaryCta?: {
    text: string;
    link: string;
  };
  reviews?: {
    count: number;
    score: number;
    platform: string;
  };
  profileImages?: string[];
}

const PageHero: React.FC<PageHeroProps> = ({
  title,
  subtitle,
  description,
  badge = "Our Services",
  backgroundImage,
  ctaText = "Get Started",
  ctaLink = "/contact",
  secondaryCta,
  reviews,
  profileImages
}) => {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 z-10 bg-zinc-950/80"></div>
        <img 
          src={backgroundImage} 
          alt="Background" 
          className="w-full h-full object-cover" 
        />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="max-w-3xl">
          <ScrollReveal>
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/20 mb-6">
              {badge}
            </span>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <h1 className="text-4xl md:text-5xl font-display font-medium text-white mb-3">
              {title}
            </h1>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <h2 className="text-2xl md:text-3xl font-display font-light text-white/90 mb-6">
              {subtitle}
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={300}>
            <p className="text-lg text-white/80 mb-8 max-w-2xl">
              {description}
            </p>
          </ScrollReveal>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <ScrollReveal delay={400}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
                <Link to={ctaLink}>
                  {ctaText}
                </Link>
              </Button>
            </ScrollReveal>
            
            {secondaryCta && (
              <ScrollReveal delay={500}>
                <Button asChild variant="outline" size="lg" className="bg-black/30 backdrop-blur-md border-white text-white hover:bg-astrem-black hover:border-astrem-black hover:text-white rounded-full">
                  <Link to={secondaryCta.link}>
                    {secondaryCta.text}
                  </Link>
                </Button>
              </ScrollReveal>
            )}
          </div>
          
          {(reviews || profileImages) && (
            <ScrollReveal delay={600}>
              <div className="flex items-center">
                {profileImages && profileImages.length > 0 && (
                  <div className="flex -space-x-4 mr-4">
                    {profileImages.map((image, index) => (
                      <div 
                        key={index}
                        className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-white overflow-hidden relative"
                        style={{ zIndex: 30 - index }}
                      >
                        <img 
                          src={image} 
                          alt={`Profile ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                {reviews && (
                  <div className="text-white">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <svg 
                          key={i} 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 24 24" 
                          fill="currentColor" 
                          className="w-4 h-4 text-yellow-400"
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-sm font-medium">
                      {reviews.score.toFixed(1)} - Based on {reviews.count} {reviews.platform} reviews
                    </p>
                  </div>
                )}
              </div>
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
